/**
 * `PhaseFSM`, finite-state machine of the global learning journey phases.
 *
 * Pure TypeScript: no React, no Studio, no DOM imports.
 *
 * Phases:
 *
 *   phase1 → phase2 → repertoire
 *
 * ## There is no `intro`
 *
 * There was: an optional harmonic intro before phase 1, the tonality and the
 * main chords of the piece shown before a note was played. It was removed for
 * good, and a journey now opens on the work itself. A payload persisted on
 * `intro` by an older bundle is read as `phase1` (see `progress-store.ts`).
 *
 * ## There is no `done`
 *
 * There was: `phase2 → phase3 → done`, where `phase3` was "performance
 * complète" and `done` was the end of the journey. Two things were wrong with
 * it. **`phase3` was unreachable** — `fullSongAtTargetTempo` had no caller
 * outside the debug overlay and the tests, so no player has ever seen the
 * screen that congratulated them. And **a terminal state is for an object
 * that gets consumed**, which a piece is not: what follows learning a piece
 * is playing it, for as long as you want to.
 *
 * `repertoire` is therefore not the end of the road, it is the road stopping
 * to move: the piece is played through, and it comes back from time to time.
 *
 * ## Why `repertoire` has no way out, and why that is not `done` again
 *
 * A first version gave it one, `backToWork()`. It had no caller outside the
 * debug overlay — the same defect the paragraph above holds against `phase3`,
 * reintroduced in the same commit that removed it. It is gone.
 *
 * The difference with `done` is not the shape of the graph, it is what the
 * state contains. `done` was terminal **and empty**: a congratulation with
 * nothing to do under it. `repertoire` is a screen with the piece on it, an
 * action that plays it, and a list of passages to take up again — and that
 * list opens a work step **without changing phase**, which is why no
 * transition is needed to go back to work. Nothing about the piece is
 * demoted, so nothing has to move.
 *
 * The FSM has explicit transition methods (one per allowed move) instead
 * of a generic `dispatch(event)` because the surface is small and naming each
 * transition makes call-sites read like domain commands rather than abstract
 * state shuffling. This contrasts with `PhraseFSM` whose event surface is
 * large and benefits from a discriminated union.
 *
 * Out-of-state calls are silently ignored: the orchestrator may receive
 * a duplicate "all phrases validated" signal (e.g. after hydration), and
 * crashing on an idempotent retry would just complicate the caller.
 *
 * See `docs/spec/parcours-apprentissage-morceau.md` §"Phases globales".
 */

export type Phase = 'phase1' | 'phase2' | 'repertoire';

export class PhaseFSM {
  private phase: Phase;
  private listeners = new Set<(phase: Phase) => void>();

  constructor(initial: Phase = 'phase1') {
    this.phase = initial;
  }

  // ── Read ───────────────────────────────────────────────────────

  getPhase(): Phase {
    return this.phase;
  }

  /**
   * Diagnostic helper: returns `true` iff the FSM can move directly to
   * `target` from the current state. Useful for UI guards (e.g. disabling
   * a "played through" button outside phase 2) without exposing the
   * transition table.
   */
  canTransition(target: Phase): boolean {
    return computeTransition(this.phase, target);
  }

  /**
   * Subscribes to phase changes. Listener is invoked once per effective
   * transition (no spurious calls when a transition is ignored).
   */
  subscribe(cb: (phase: Phase) => void): () => void {
    this.listeners.add(cb);

    return () => {
      this.listeners.delete(cb);
    };
  }

  // ── Transitions ────────────────────────────────────────────────

  /** phase1 → phase2. Emitted when every phrase has reached `DONE`. */
  allPhrasesValidated(): void {
    this.transitionTo('phase2', 'phase1');
  }

  /**
   * phase2 → repertoire. Emitted the first time the piece is played through
   * end to end — at whatever tempo the player is on.
   *
   * Not "at the target tempo", which used to be the condition and cannot be
   * one: the written tempo is exactly 120 in **67.1% of PDMX (302/450)**, the
   * MuseScore default, and the climb to it is **2 rungs at the p10 and 10 at
   * the p90** — with **5.0% of pieces (10/200) already on the last rung
   * before a note is played**. A finish line that means two sessions for one
   * player and six months for another, for reasons that have nothing to do
   * with how they play, is not a finish line. The offer to go faster stays,
   * inside the repertoire, and never expires.
   */
  playedThrough(): void {
    this.transitionTo('repertoire', 'phase2');
  }

  /** Unconditional reset back to `phase1`. Used by `LearningJourney.reset()`. */
  reset(): void {
    if (this.phase === 'phase1') {
      return;
    }

    this.phase = 'phase1';
    this.notify();
  }

  // ── Internal ───────────────────────────────────────────────────

  private transitionTo(target: Phase, expectedFrom: Phase): void {
    if (this.phase !== expectedFrom) {
      return;
    }

    this.phase = target;
    this.notify();
  }

  private notify(): void {
    const phase = this.phase;
    this.listeners.forEach((cb) => {
      cb(phase);
    });
  }
}

/**
 * Allowed transitions table. Kept as a flat function rather than a Map
 * so the file is greppable (every `from === X && to === Y` is visible).
 */
function computeTransition(from: Phase, to: Phase): boolean {
  if (from === 'phase1' && to === 'phase2') {
    return true;
  }

  if (from === 'phase2' && to === 'repertoire') {
    return true;
  }

  return false;
}
